import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Select, Spin, Tooltip } from 'antd'
import classNames from 'classnames'
import shallow from 'zustand/shallow'
import { useTranslation } from 'react-i18next'
import moment from 'moment-timezone'
import PerText from '../../../components/custom_components/Text'
import PerIcon from '../../../components/custom_components/Icon'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import { IconNames } from '../../../components/util/types'
import { useAuth } from '../../../context/auth-context'
import { useCompanyManagerDashboardWorkforceCounters } from '../../../graphql/company-manager-dashboard/hooks/useCompanyManagerDashboardWorkforceQueries'
import { useDashboardStore } from '../DashboardStore'
import { buildHeaderCountersFromDto, countIntegerDigits } from '../utils/dashboardHeaderCountersUtil'
import { WorkforceMovementPeriodEnum, RoleName } from '../../../types'
import DashboardCounterWidgetHeader from './DashboardCounterWidgetHeader'
import InfoTooltipButton from './InfoTooltipButton'
import { DashboardWorkforceMovementModal, DashboardWorkforceMovementModalSelection } from './DashboardWorkforceMovementModal'
import '../styles/dashboard-header-counters.less'
import { useCompanyManagerDashboardPartialVisibility } from '../../../graphql/company-manager-dashboard/hooks/useCompanyManagerDashboardPartialVisibilityQuery'
import { IconIndicator } from '../../../components/custom_components/IconIndicator/IconIndicator'

const { Option } = Select

type RcSelectHandle = {
  rcSelect?: {
    setInputValue: (value: string, fireSearch?: boolean) => void
    getInputDOMNode?: () => HTMLInputElement
  }
}

const resetCompanySelectSearchInput = (select: Select | null, placeholder: string): void => {
  const rcSelect = (select as unknown as RcSelectHandle | null)?.rcSelect
  if (!rcSelect) {
    return
  }

  rcSelect.setInputValue('', true)

  const input =
    rcSelect.getInputDOMNode?.() ??
    document.querySelector<HTMLInputElement>('.dashboard-hc__company-select--searchable.ant-select-open .ant-select-search__field')
  if (!input) {
    return
  }

  input.value = ''
  input.placeholder = placeholder
}

const DashboardHeaderCounters: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [companiesForSelection, selected, selectedDashboardRoles, setSelected] = useDashboardStore(
    (s) => [s.companiesForSelection, s.selected, s.selectedDashboardRoles, s.setSelected],
    shallow,
  )
  const companyId = selected.value
  const { data, loading, error } = useCompanyManagerDashboardWorkforceCounters(companyId, selectedDashboardRoles)
  const { data: partialVisibilityData } = useCompanyManagerDashboardPartialVisibility(companyId, selectedDashboardRoles)

  const countersDto = data?.companyManagerDashboardWorkforceCounters

  const display = useMemo(() => {
    if (!countersDto) {
      return buildHeaderCountersFromDto(undefined, 0)
    }
    return buildHeaderCountersFromDto(countersDto.workforceMovementCounters, countersDto.totalEmployees)
  }, [countersDto])

  const multiCompany = companiesForSelection.length > 1
  const isHrUser = !!user.roles?.includes(RoleName.Hr)
  const companySelectSearchEnabled = multiCompany && isHrUser
  const totalEmployeesValue = display.totalEmployees
  const totalEmployeesManyDigits = countIntegerDigits(totalEmployeesValue) > 3
  const countersAvailable = !!countersDto && !loading && !error
  const partialVisibility = partialVisibilityData?.companyManagerDashboardPartialVisibility || null

  const [workforceModal, setWorkforceModal] = useState<DashboardWorkforceMovementModalSelection | null>(null)
  const companySelectRef = useRef<Select>(null)

  const companySelectSearchPlaceholder = t('dashboard.companyManager.header.companySelectSearchPlaceholder')

  const openCompanySelectSearch = () => {
    const reset = () => resetCompanySelectSearchInput(companySelectRef.current, companySelectSearchPlaceholder)
    requestAnimationFrame(() => {
      reset()
      requestAnimationFrame(reset)
    })
  }

  const workforceMonthLabelLong = (period: WorkforceMovementPeriodEnum): string => {
    const m = moment.tz(user.timezone)
    if (period === WorkforceMovementPeriodEnum.PreviousMonth) {
      return m.clone().subtract(1, 'month').format('MMMM')
    }
    if (period === WorkforceMovementPeriodEnum.NextMonth) {
      return m.clone().add(1, 'month').format('MMMM')
    }
    return m.format('MMMM')
  }

  useEffect(() => {
    if (!error) {
      return
    }
    showNotification({
      type: 'error',
      message: t('dashboard.companyManager.header.countersQueryError.title'),
      description: t('dashboard.companyManager.header.countersQueryError.description'),
    })
  }, [error, t])

  return (
    <div className="dashboard-hc">
      <div className="dashboard-hc__company">
        <PerFlexRow align="middle" noWrap width="fullWidth">
          {!!partialVisibility && (
            <Tooltip title={t('dashboard.companyManager.header.partialCountryVisibilityTooltip')}>
              <span
                className="dashboard-hc__partial-visibility-icon"
                role="img"
                aria-label={t('dashboard.companyManager.header.partialCountryVisibilityAria')}
              >
                <IconIndicator icon="warning_fill" background="default" size="m" iconSize={10} />
              </span>
            </Tooltip>
          )}
          {multiCompany ? (
            <Select
              ref={companySelectRef}
              className={classNames('dashboard-hc__company-select', {
                'dashboard-hc__company-select--searchable': companySelectSearchEnabled,
              })}
              value={selected.value}
              onChange={(value) => setSelected(String(value))}
              getPopupContainer={() => document.body}
              aria-label={t('dashboard.companyManager.header.companySelectAria')}
              showSearch={companySelectSearchEnabled}
              optionFilterProp="label"
              onDropdownVisibleChange={(open) => {
                if (companySelectSearchEnabled && open) {
                  openCompanySelectSearch()
                }
              }}
              placeholder={companySelectSearchEnabled ? companySelectSearchPlaceholder : undefined}
            >
              {companiesForSelection.map((c) => (
                <Option key={c.value} value={c.value} label={c.label}>
                  {c.label}
                </Option>
              ))}
            </Select>
          ) : (
            <PerText fontSize="m" fontWeight="bold" className="dashboard-hc__company-text">
              {selected.label}
            </PerText>
          )}
        </PerFlexRow>
      </div>

      <div className="dashboard-hc__widgets" role="group" aria-label={t('dashboard.companyManager.header.countersGroupAria')}>
        <div className="dashboard-hc__widget">
          <div role="group" aria-label={t('dashboard.companyManager.header.totalEmployees.label')}>
            <PerFlexRow justify="start" align="middle" noWrap width="fullWidth">
              <PerText
                className={
                  totalEmployeesManyDigits ? 'dashboard-hc__value--total dashboard-hc__value--total--compact' : 'dashboard-hc__value--total'
                }
                testId="dashboard-hc-total"
              >
                {countersAvailable ? (
                  totalEmployeesValue
                ) : loading ? (
                  <Spin />
                ) : (
                  <PerText fontSize="m" fontWeight="bold">
                    N/A
                  </PerText>
                )}
              </PerText>
              <PerFlexRow direction="column" align="top" justify="start" ml="xxs">
                <PerIcon name="dashboard_totalEmployees" color="primary" size={22} />
                <PerFlexRow direction="column" justify="start" mt="xxxs">
                  <PerText transform="uppercase" fontSize="xs">
                    {t('dashboard.companyManager.header.totalEmployees.labelFirstLine')}
                  </PerText>
                  <PerText transform="uppercase" fontSize="xs">
                    {t('dashboard.companyManager.header.totalEmployees.labelSecondLine')}
                  </PerText>
                </PerFlexRow>
              </PerFlexRow>
            </PerFlexRow>
          </div>
        </div>

        <div className="dashboard-hc__widget">
          <SplitStatWidget
            icon="dashboard_newEntries"
            mainValue={display.newEntries.main}
            labelFirstLine={t('dashboard.companyManager.header.newEntries.labelFirstLine')}
            labelSecondLine={t('dashboard.companyManager.header.newEntries.labelSecondLine')}
            infoTooltip={t('dashboard.companyManager.header.newEntries.infoTooltip')}
            subLeft={{
              value: display.newEntries.previousMonth,
              label: t('dashboard.companyManager.header.newEntries.subPreviousMonth'),
              onClick: countersAvailable
                ? () =>
                    setWorkforceModal({
                      view: 'single',
                      kind: 'newEntries',
                      period: WorkforceMovementPeriodEnum.PreviousMonth,
                      monthLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.PreviousMonth),
                      subCounterValue: display.newEntries.previousMonth,
                      header: {
                        icon: 'dashboard_newEntries',
                        mainValue: display.newEntries.main,
                        label: t('dashboard.companyManager.header.newEntries.label'),
                      },
                    })
                : undefined,
              openDetailsAria: t('dashboard.companyManager.header.workforceModal.openDetailsAria', {
                month: t('dashboard.companyManager.header.newEntries.subPreviousMonth'),
              }),
            }}
            subRight={{
              value: display.newEntries.currentMonth,
              label: t('dashboard.companyManager.header.newEntries.subCurrentMonth'),
              onClick: countersAvailable
                ? () =>
                    setWorkforceModal({
                      view: 'single',
                      kind: 'newEntries',
                      period: WorkforceMovementPeriodEnum.CurrentMonth,
                      monthLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.CurrentMonth),
                      subCounterValue: display.newEntries.currentMonth,
                      header: {
                        icon: 'dashboard_newEntries',
                        mainValue: display.newEntries.main,
                        label: t('dashboard.companyManager.header.newEntries.label'),
                      },
                    })
                : undefined,
              openDetailsAria: t('dashboard.companyManager.header.workforceModal.openDetailsAria', {
                month: t('dashboard.companyManager.header.newEntries.subCurrentMonth'),
              }),
            }}
            countersAvailable={countersAvailable}
            loading={loading}
            onMainClick={
              countersAvailable
                ? () =>
                    setWorkforceModal({
                      view: 'split',
                      kind: 'newEntries',
                      firstPeriodLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.PreviousMonth),
                      secondPeriodLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.CurrentMonth),
                      firstSubValue: display.newEntries.previousMonth,
                      secondSubValue: display.newEntries.currentMonth,
                      header: {
                        icon: 'dashboard_newEntries',
                        mainValue: display.newEntries.main,
                        label: t('dashboard.companyManager.header.newEntries.label'),
                      },
                    })
                : undefined
            }
            mainOpenDetailsAria={t('dashboard.companyManager.header.workforceModal.openMainDetailsAria', {
              label: t('dashboard.companyManager.header.newEntries.label'),
            })}
          />
        </div>

        <div className="dashboard-hc__widget">
          <SplitStatWidget
            icon="dashboard_employeeExit"
            mainValue={display.employeeExits.main}
            labelFirstLine={t('dashboard.companyManager.header.employeeExits.labelFirstLine')}
            labelSecondLine={t('dashboard.companyManager.header.employeeExits.labelSecondLine')}
            infoTooltip={t('dashboard.companyManager.header.employeeExits.infoTooltip')}
            subLeft={{
              value: display.employeeExits.currentMonth,
              label: t('dashboard.companyManager.header.employeeExits.subCurrentMonth'),
              onClick: countersAvailable
                ? () =>
                    setWorkforceModal({
                      view: 'single',
                      kind: 'employeeExits',
                      period: WorkforceMovementPeriodEnum.CurrentMonth,
                      monthLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.CurrentMonth),
                      subCounterValue: display.employeeExits.currentMonth,
                      header: {
                        icon: 'dashboard_employeeExit',
                        mainValue: display.employeeExits.main,
                        label: t('dashboard.companyManager.header.employeeExits.label'),
                      },
                    })
                : undefined,
              openDetailsAria: t('dashboard.companyManager.header.workforceModal.openDetailsAria', {
                month: t('dashboard.companyManager.header.employeeExits.subCurrentMonth'),
              }),
            }}
            subRight={{
              value: display.employeeExits.nextMonth,
              label: t('dashboard.companyManager.header.employeeExits.subNextMonth'),
              onClick: countersAvailable
                ? () =>
                    setWorkforceModal({
                      view: 'single',
                      kind: 'employeeExits',
                      period: WorkforceMovementPeriodEnum.NextMonth,
                      monthLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.NextMonth),
                      subCounterValue: display.employeeExits.nextMonth,
                      header: {
                        icon: 'dashboard_employeeExit',
                        mainValue: display.employeeExits.main,
                        label: t('dashboard.companyManager.header.employeeExits.label'),
                      },
                    })
                : undefined,
              openDetailsAria: t('dashboard.companyManager.header.workforceModal.openDetailsAria', {
                month: t('dashboard.companyManager.header.employeeExits.subNextMonth'),
              }),
            }}
            countersAvailable={countersAvailable}
            loading={loading}
            onMainClick={
              countersAvailable
                ? () =>
                    setWorkforceModal({
                      view: 'split',
                      kind: 'employeeExits',
                      firstPeriodLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.CurrentMonth),
                      secondPeriodLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.NextMonth),
                      firstSubValue: display.employeeExits.currentMonth,
                      secondSubValue: display.employeeExits.nextMonth,
                      header: {
                        icon: 'dashboard_employeeExit',
                        mainValue: display.employeeExits.main,
                        label: t('dashboard.companyManager.header.employeeExits.label'),
                      },
                    })
                : undefined
            }
            mainOpenDetailsAria={t('dashboard.companyManager.header.workforceModal.openMainDetailsAria', {
              label: t('dashboard.companyManager.header.employeeExits.label'),
            })}
          />
        </div>

        <div className="dashboard-hc__widget">
          <SplitStatWidget
            icon="dashboard_probationEnd"
            mainValue={display.probationEnd.main}
            labelFirstLine={t('dashboard.companyManager.header.probationEnd.labelFirstLine')}
            labelSecondLine={t('dashboard.companyManager.header.probationEnd.labelSecondLine')}
            infoTooltip={t('dashboard.companyManager.header.probationEnd.infoTooltip')}
            subLeft={{
              value: display.probationEnd.currentMonth,
              label: t('dashboard.companyManager.header.probationEnd.subCurrentMonth'),
              onClick: countersAvailable
                ? () =>
                    setWorkforceModal({
                      view: 'single',
                      kind: 'probationEnd',
                      period: WorkforceMovementPeriodEnum.CurrentMonth,
                      monthLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.CurrentMonth),
                      subCounterValue: display.probationEnd.currentMonth,
                      header: {
                        icon: 'dashboard_probationEnd',
                        mainValue: display.probationEnd.main,
                        label: t('dashboard.companyManager.header.probationEnd.label'),
                      },
                    })
                : undefined,
              openDetailsAria: t('dashboard.companyManager.header.workforceModal.openDetailsAria', {
                month: t('dashboard.companyManager.header.probationEnd.subCurrentMonth'),
              }),
            }}
            subRight={{
              value: display.probationEnd.nextMonth,
              label: t('dashboard.companyManager.header.probationEnd.subNextMonth'),
              onClick: countersAvailable
                ? () =>
                    setWorkforceModal({
                      view: 'single',
                      kind: 'probationEnd',
                      period: WorkforceMovementPeriodEnum.NextMonth,
                      monthLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.NextMonth),
                      subCounterValue: display.probationEnd.nextMonth,
                      header: {
                        icon: 'dashboard_probationEnd',
                        mainValue: display.probationEnd.main,
                        label: t('dashboard.companyManager.header.probationEnd.label'),
                      },
                    })
                : undefined,
              openDetailsAria: t('dashboard.companyManager.header.workforceModal.openDetailsAria', {
                month: t('dashboard.companyManager.header.probationEnd.subNextMonth'),
              }),
            }}
            countersAvailable={countersAvailable}
            loading={loading}
            onMainClick={
              countersAvailable
                ? () =>
                    setWorkforceModal({
                      view: 'split',
                      kind: 'probationEnd',
                      firstPeriodLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.CurrentMonth),
                      secondPeriodLabel: workforceMonthLabelLong(WorkforceMovementPeriodEnum.NextMonth),
                      firstSubValue: display.probationEnd.currentMonth,
                      secondSubValue: display.probationEnd.nextMonth,
                      header: {
                        icon: 'dashboard_probationEnd',
                        mainValue: display.probationEnd.main,
                        label: t('dashboard.companyManager.header.probationEnd.label'),
                      },
                    })
                : undefined
            }
            mainOpenDetailsAria={t('dashboard.companyManager.header.workforceModal.openMainDetailsAria', {
              label: t('dashboard.companyManager.header.probationEnd.label'),
            })}
          />
        </div>
      </div>

      <DashboardWorkforceMovementModal
        open={workforceModal !== null}
        onClose={() => setWorkforceModal(null)}
        companyId={companyId}
        selection={workforceModal}
      />
    </div>
  )
}

type SplitSub = { value: number; label: string; onClick?: () => void; openDetailsAria?: string }

type SplitStatWidgetProps = {
  icon: IconNames
  mainValue: number
  labelFirstLine: string
  labelSecondLine: string
  infoTooltip: string
  subLeft: SplitSub
  subRight: SplitSub
  countersAvailable: boolean
  loading: boolean
  onMainClick?: () => void
  mainOpenDetailsAria?: string
}

const SubStatPair: FC<{ data: SplitSub }> = ({ data }) => {
  const content = (
    <PerFlexRow align="middle" noWrap className="fullHeight">
      <PerText className="dashboard-hc__sub-val">{data.value}</PerText>
      <PerText className="dashboard-hc__sub-lab">{data.label}</PerText>
    </PerFlexRow>
  )
  if (!data.onClick) {
    return content
  }
  return (
    <div
      className="dashboard-hc__sub-stat--clickable"
      onClick={data.onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          data.onClick?.()
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={data.openDetailsAria}
    >
      {content}
    </div>
  )
}

const SplitStatWidget: FC<SplitStatWidgetProps> = ({
  icon,
  mainValue,
  labelFirstLine,
  labelSecondLine,
  infoTooltip,
  subLeft,
  subRight,
  countersAvailable,
  loading,
  onMainClick,
  mainOpenDetailsAria,
}) => {
  return (
    <PerFlexRow direction="column">
      <PerFlexRow justify="space-between" align="top" noWrap width="fullWidth">
        <DashboardCounterWidgetHeader
          icon={icon}
          totalValue={mainValue}
          labelFirstLine={labelFirstLine}
          labelSecondLine={labelSecondLine}
          countersAvailable={countersAvailable}
          loading={loading}
          onClick={onMainClick}
          openDetailsAria={mainOpenDetailsAria}
        />
        <InfoTooltipButton title={infoTooltip} />
      </PerFlexRow>

      <hr className="dashboard-hc__subrow-sep" aria-hidden />

      <PerFlexRow justify="space-between" align="middle" noWrap width="fullWidth">
        {countersAvailable && <SubStatPair data={subLeft} />}
        {countersAvailable && <SubStatPair data={subRight} />}
      </PerFlexRow>
    </PerFlexRow>
  )
}

export default DashboardHeaderCounters
